import { createContext, useContext, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { getUtm, track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DemoFormContext = createContext(null);
export const useDemoForm = () => useContext(DemoFormContext);

export function DemoFormProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState({ source: "", prefill: "" });

  const openDialog = (s = "unknown", prefill = "") => {
    setConfig({ source: s, prefill });
    track("demo_cta_click", { source: s });
    setOpen(true);
    window.__lenis?.stop?.();
  };
  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) window.__lenis?.start?.();
  };

  return (
    <DemoFormContext.Provider value={{ openDialog }}>
      {children}
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="demo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold tracking-tight text-navy">
              Request a Workflow Demonstration
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              See the VeraChart prompt workflow live. We'll follow up shortly after your request.
            </DialogDescription>
          </DialogHeader>
          <DemoForm source={config.source} defaultMessage={config.prefill} key={config.source + config.prefill} />
        </DialogContent>
      </Dialog>
    </DemoFormContext.Provider>
  );
}

const inputCls =
  "w-full rounded-md border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20";
const labelCls = "mb-1.5 block text-sm font-medium text-slate-700";

const TEAM_COUNTS = ["1", "2", "3", "4–5", "6–10", "11+"];
const TEAM_SIZES = ["1–5", "6–10", "11–20", "More than 20"];
const PROGRAM_TYPES = ["ACT", "PACT", "FACT", "Multiple", "Other"];

function formatApiError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
}

export function DemoForm({ source = "inline", defaultMessage = "" }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const startedTracked = useRef(false);
  const mountedAt = useRef(Date.now());
  const tid = source === "demo-section" ? "inline-demo-form" : "demo-form";

  const handleFirstFocus = () => {
    if (!startedTracked.current) {
      startedTracked.current = true;
      track("demo_form_start", { source });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const f = e.currentTarget;
    const val = (name) => f.elements[name]?.value?.trim() || "";
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: val("first_name"),
          last_name: val("last_name"),
          email: val("email"),
          organization: val("organization"),
          job_title: val("job_title"),
          team_count: val("team_count"),
          team_size: val("team_size"),
          program_type: val("program_type"),
          message: val("message"),
          company_website: val("company_website"),
          elapsed_ms: Date.now() - mountedAt.current,
          ...getUtm(),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(formatApiError(data.detail));
      }
      track("demo_form_submit", { source });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-start gap-4 rounded-lg border border-teal-200 bg-teal-50 p-8" data-testid={`${tid}-success`}>
        <CheckCircle2 className="h-10 w-10 text-teal-600" aria-hidden="true" />
        <p className="font-display text-xl font-bold text-navy">Thank you.</p>
        <p className="text-slate-600">
          We've received your request and will be in touch shortly. If your matter is urgent, you can also reach us at{" "}
          <a href="mailto:sales@verachart.org" className="font-medium text-teal-700 underline underline-offset-2">
            sales@verachart.org
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid={tid} aria-label="Request a workflow demonstration">
      {/* honeypot — hidden from humans */}
      <div aria-hidden="true" className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden">
        <label htmlFor={`company_website_${source}`}>Company website</label>
        <input id={`company_website_${source}`} name="company_website" type="text" tabIndex={-1} autoComplete="off" />
      </div>

      <div onFocusCapture={handleFirstFocus} className="contents">
        <div>
          <label htmlFor={`first_name_${source}`} className={labelCls}>First Name *</label>
          <input id={`first_name_${source}`} name="first_name" required maxLength={100} className={inputCls} data-testid={`${tid}-first-name`} autoComplete="given-name" />
        </div>
        <div>
          <label htmlFor={`last_name_${source}`} className={labelCls}>Last Name *</label>
          <input id={`last_name_${source}`} name="last_name" required maxLength={100} className={inputCls} data-testid={`${tid}-last-name`} autoComplete="family-name" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor={`email_${source}`} className={labelCls}>Work Email *</label>
          <input id={`email_${source}`} name="email" type="email" required maxLength={200} className={inputCls} data-testid={`${tid}-email`} autoComplete="email" placeholder="you@agency.org" />
        </div>
        <div>
          <label htmlFor={`organization_${source}`} className={labelCls}>Organization *</label>
          <input id={`organization_${source}`} name="organization" required maxLength={200} className={inputCls} data-testid={`${tid}-organization`} autoComplete="organization" />
        </div>
        <div>
          <label htmlFor={`job_title_${source}`} className={labelCls}>Job Title *</label>
          <input id={`job_title_${source}`} name="job_title" required maxLength={150} className={inputCls} data-testid={`${tid}-job-title`} autoComplete="organization-title" />
        </div>
        <div>
          <label htmlFor={`team_count_${source}`} className={labelCls}>Number of ACT/PACT/FACT Teams *</label>
          <select id={`team_count_${source}`} name="team_count" required defaultValue="" className={inputCls} data-testid={`${tid}-team-count`}>
            <option value="" disabled>Select…</option>
            {TEAM_COUNTS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={`program_type_${source}`} className={labelCls}>Primary Program Type *</label>
          <select id={`program_type_${source}`} name="program_type" required defaultValue="" className={inputCls} data-testid={`${tid}-program-type`}>
            <option value="" disabled>Select…</option>
            {PROGRAM_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={`team_size_${source}`} className={labelCls}>Approximate Team Size *</label>
          <select id={`team_size_${source}`} name="team_size" required defaultValue="" className={inputCls} data-testid={`${tid}-team-size`}>
            <option value="" disabled>Select…</option>
            {TEAM_SIZES.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor={`message_${source}`} className={labelCls}>Message <span className="font-normal text-slate-400">(optional)</span></label>
          <textarea id={`message_${source}`} name="message" rows={3} maxLength={3000} defaultValue={defaultMessage} className={inputCls} data-testid={`${tid}-message`} placeholder="Which suite or prompts are you interested in?" />
        </div>
      </div>

      <div className="sm:col-span-2">
        <Button type="submit" disabled={submitting} data-testid={`${tid}-submit-button`}
          className="h-12 w-full rounded-md bg-teal-600 px-8 font-display text-base font-semibold text-white shadow-md transition-colors hover:bg-teal-700 disabled:opacity-60 sm:w-auto">
          {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />Submitting…</>) : "Request Demonstration"}
        </Button>
        <p className="mt-3 text-xs text-slate-500">
          No obligation. We'll respond to your request directly — no automated sequences.
        </p>
      </div>
    </form>
  );
}
